import React from 'react';

import { Text } from '../../../../components/Text';
import { Anchor, Tip } from './CtaHint.styles';

/**
 * Wraps the auth CTA and, while it is unavailable, explains WHY on hover.
 *
 * Both auth screens gate their button on the same thing, a well-formed email, so the
 * copy lives here rather than in each page.
 *
 * "Disabled" actually covers two different situations, and a single message would be
 * wrong in one of them: telling someone their address is invalid before they have typed
 * anything is misleading, and telling someone to enter an email when they already typed
 * `foo@` explains nothing. So the copy follows the field, the same blank-vs-malformed
 * split the contact form already makes (`pages/contact/useContactForm.ts`), reusing its
 * two strings verbatim rather than minting near-identical keys. The catalog already
 * carries three variants of "enter a valid email"; it does not need a fourth.
 *
 * The tip is `aria-describedby`-linked from the button by the caller. A disabled button
 * is not focusable, so nothing here can surface on keyboard focus, the association is
 * what lets a screen reader reach the reason in browse mode. Sighted keyboard users get
 * the same information from the field's own validation, not from this tip.
 */

/** Ties the tip to the CTA via `aria-describedby`; the caller sets it on the button. */
export const CTA_HINT_ID = 'auth-cta-hint';

export type CtaHintProps = {
  /** Current field value. Empty (or whitespace) selects the "not started" copy. */
  email: string | undefined;
  /** Whether the CTA is currently unavailable. The tip is absent otherwise, so no
   *  stale hint can linger over a live button. */
  disabled: boolean;
  /** The CTA itself. */
  children: React.ReactNode;
};

export const CtaHint = ({ email, disabled, children }: CtaHintProps): React.ReactElement => {
  const hasTyped = (email ?? '').trim().length > 0;

  return (
    <Anchor>
      {children}
      {disabled ? (
        <Tip role="tooltip" id={CTA_HINT_ID}>
          {hasTyped ? (
            <Text text="Please enter a valid email address" />
          ) : (
            <Text text="Please enter your email address" />
          )}
        </Tip>
      ) : null}
    </Anchor>
  );
};

export default CtaHint;
